import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ExcelService, ExcelSheet } from 'src/app/shared/excel/excel-style.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { keyBy } from 'lodash';
import { BatteryMetricItem } from '../utilization-reports/widgets/utilization-metric-box/utilization-metrics.interface';
import { SiteDashboardService } from '../../../site-dashboard.service';
import { DeviceCommonService } from '../../device-common.service';

@Component({
	selector: 'app-charger-exception-reports',
	templateUrl: './charger-exception-reports.component.html',
	styleUrls: ['./charger-exception-reports.component.css']
})
export class ChargerExceptionReportsComponent implements OnInit, OnDestroy{

	siteSub: Subscription = new Subscription();
	siteInfo: any;
	devices: any[] = [];

	readonly periods = [
		{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
		{ value: 'last_14_days', label: this.translateService.instant('durations.last_14_days') },
		{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
		{ value: 'since_installation', label: this.translateService.instant('durations.all_years') },
	];
	voltagesOptions: any[] = [];
	manufacturersOptions: any[] = [];
	vintageOptions = this.siteDashboardService.vintageOptionsDropdown;
	
	assetUtilization: Array<{ label: string; value: number, showPercentage: boolean, percentage: number }> = [];
	maintenance: Array<{ label: string; value: number, showPercentage: boolean, percentage: number }> = [];

	lowChargerUtilization: Record<string, any> = {};
	lowChargerAhUtilization: Record<string, any> = {};
	lowChargerKwhUtilization: Record<string, any> = {};
	highChargerKwUtilization: Record<string, any> = {};
	disconnectedChargers: Record<string, any> = {};
	chargersOutsideGeofence: Record<string, any> = {};

	siteSettings: any = {};

	readonly staticColumnConfig = [
		{ key: 'serial_number', name: this.translateService.instant('g.charglink_sn'), type: "link" },
		{ key: 'charger_id', name: this.translateService.instant('devices.charger_id'), type: "link" },
		{ key: 'charger_sn', name: this.translateService.instant('devices.charger_sn'), type: "string" },
		{ key: 'truck_manufacturer', name: this.translateService.instant('devices.charger_manufacturer'), type: "string" },
		{ key: 'truck_type', name: this.translateService.instant('devices.charger_model'), type: "string" },
	];

	widgetsConfig: Record<string, any> = {
		low_charger_utilization: {
			title: this.translation.instant('exception_reports.low_charger_utilization'),
			tooltip: 'low_charger_utilization_tooltip',
			name: 'low_charger_utilization',
			has_period_filter: true,
			has_voltage_filter: true,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			extra_fields: ['charger_utilization_score'],
			staticColumnConfig: this.staticColumnConfig,
		},
		low_charger_ah_utilization: {
			title: this.translation.instant('exception_reports.low_charger_ah_utilization'),
			tooltip: 'low_charger_ah_utilization_tooltip',
			name: 'low_charger_ah_utilization',
			has_period_filter: true,
			has_voltage_filter: true,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			extra_fields: ['charger_ah_utilization_score'],
			staticColumnConfig: this.staticColumnConfig,
		},
		low_charger_kwh_utilization: {
			title: this.translation.instant('exception_reports.low_charger_kwh_utilization'),
			tooltip: 'low_charger_kwh_utilization_tooltip',
			name: 'low_charger_kwh_utilization',
			has_period_filter: true,
			has_voltage_filter: true,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			extra_fields: ['charger_kwh_utilization_score'],
			staticColumnConfig: this.staticColumnConfig,
		},
		high_charger_kw_utilization: {
			title: this.translation.instant('exception_reports.high_charger_kw_utilization'),
			tooltip: 'high_charger_kw_utilization_tooltip',
			name: 'high_charger_kw_utilization',
			has_period_filter: true,
			has_voltage_filter: true,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			extra_fields: ['charger_kw_utilization'],
			staticColumnConfig: this.staticColumnConfig,
		},
		disconnected_chargers: {
			title: this.translation.instant('exception_reports.disconnected_chargers'),
			tooltip: 'disconnected_chargers_tooltip',
			name: 'disconnected_chargers',
			has_period_filter: true,
			has_voltage_filter: true,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			extra_fields: ['days_charger_disconnected'],
			staticColumnConfig: this.staticColumnConfig,
		},
		chargers_outside_geofence: {
			title: this.translation.instant('exception_reports.chargers_outside_geofence'),
			tooltip: 'chargers_outside_geofence_tooltip',
			name: 'chargers_outside_geofence',
			has_voltage_filter: true,
			has_manufacturer_filter: true,
			extra_fields: ['last_charger_location_long', 'last_charger_location_lat', 'days_outside_geofence'],
			staticColumnConfig: this.staticColumnConfig,
		},
	};

	widgetsDataMapper: Record<string, string> = {
		low_charger_utilization: 'lowChargerUtilization',
		low_charger_ah_utilization: 'lowChargerAhUtilization',
		low_charger_kwh_utilization: 'lowChargerKwhUtilization',
		high_charger_kw_utilization: 'highChargerKwUtilization',
		disconnected_chargers: 'disconnectedChargers',
		chargers_outside_geofence: 'chargersOutsideGeofence',
	};

	PERCENT_WIDGETS = new Set([
		'low_charger_utilization',
		'low_charger_ah_utilization',
		'low_charger_kwh_utilization',
		'high_charger_kw_utilization',
	]);

	filters: {
		selectedVoltage: string;
		selectedPeriod: string;
		selectedVintage: string[];
		selectedManufacturer: string;
	} = {
			selectedVoltage: 'all',
			selectedPeriod: this.periods[0].value,
			selectedVintage: [this.siteDashboardService.vintageOptionsDropdown[0].value],
			selectedManufacturer: 'all',
		};

	constructor (
		private translation: TranslateService,
		private siteService: SitesService,
		private route: ActivatedRoute,
		private sideMenuService: SideMenuService,
		public commonService: CommonService,
		private translateService: TranslateService,
		private excelService: ExcelService,
		public siteDashboardService: SiteDashboardService,
		private deviceCommonService: DeviceCommonService,
	) {}

	ngOnInit() {
		const siteId = this.route?.parent?.snapshot?.params?.siteId || 0;
		if (!siteId)
			return;

		this.siteSub = this.sideMenuService.currentSite.subscribe((site: any) => {
			if (site) {
				this.commonService.setPageTabTitle(`${site['customer_name']} - ${site['name']} - ${this.translation.instant('exception_reports.charger_exception_reports')}`)
				this.siteInfo = site;

				this.getChargerExceptionReports();
			}
		});
	}

	getChargerExceptionReports() {
		this.siteService.getChargerExceptionReports(this.siteInfo.id).subscribe((response: any) => {
			this.devices = response.devices || [];
			this.siteSettings = response.charger_utilization_settings || {};

			this.lowChargerUtilization = response.low_charger_utilization || [];
			this.lowChargerAhUtilization = response.low_charger_ah_utilization || [];
			this.lowChargerKwhUtilization = response.low_charger_kwh_utilization || [];
			this.highChargerKwUtilization = response.high_charger_kw_utilization || [];
			this.disconnectedChargers = response.disconnected_chargers || [];
			this.chargersOutsideGeofence = response.chargers_outside_geofence || [];

			this.prepareOptions();
			this.formatUtilizationData();
		});
	}

	prepareOptions() {
		const voltages: number[] = [];
		const manufacturers: string[] = [];

		for (const device of this.devices) {
			if (!voltages.includes(device.charger_voltage))
				voltages.push(device.charger_voltage);

			if (!manufacturers.includes(device.truck_manufacturer))
				manufacturers.push(device.truck_manufacturer);
		}

		this.voltagesOptions = [
			{ value: 'all', label: this.translateService.instant('g.all') },
			...voltages.map(value => ({ value, label: this.deviceCommonService.getChargerVoltageLabel(value) })),
		];

		this.manufacturersOptions = [{ value: 'all', label: this.translateService.instant('g.all') }, ...manufacturers.map(value => ({ value, label: value }))];
	}

	formatUtilizationData() {
		this.filters = structuredClone(this.filters);

		const assetUtilizationData = [
			'low_charger_utilization',
			'low_charger_ah_utilization',
			'low_charger_kwh_utilization',
			'chargers_outside_geofence',
		]

		const assetUtilization = [];

		for (const type of assetUtilizationData) {
			const value = this.calculateScore(type);

			assetUtilization.push({
				label: this.translation.instant(`utilization_metrics.${type}`),
				value: value,
				percentage: value,
				showPercentage: true,
				name: type,
			});
		}

		this.assetUtilization = assetUtilization;
		this.calculateMaintenanceMetrics();
	}
	
	calculateMaintenanceMetrics() {
		const {
			low_disconnect_range_max,
			low_disconnect_range_min,
			mid_disconnect_range_max,
			mid_disconnect_range_min,
			high_disconnect_limit,
		} = this.siteSettings;

		const maintenanceData: { [key: string]: { value: number, label: string } } = {
			low_disconnected_devices: { value: 0, label: `${this.translation.instant(`exception_reports.charger_disconnections`)} ${low_disconnect_range_min}-${low_disconnect_range_max} ${this.translation.instant('g.days')}`},
			mid_disconnected_devices: { value: 0, label: `${this.translation.instant(`exception_reports.charger_disconnections`)} ${mid_disconnect_range_min}-${mid_disconnect_range_max} ${this.translation.instant('g.days')}`},
			high_disconnected_devices: { value: 0, label: `${this.translation.instant(`exception_reports.charger_disconnections`)} > ${high_disconnect_limit} ${this.translation.instant('g.days')}`},
		};

		const disconnectedDevices = this.disconnectedChargers[this.filters.selectedPeriod] || {};
		const totalDevicesCount = this.devices.filter(device => device.mac_address in disconnectedDevices && this.isIncludedDevice(device.mac_address)).length;

		for (const macAddress in disconnectedDevices) {
			if (!this.isIncludedDevice(macAddress) || !disconnectedDevices[macAddress]?.showDevice)
				continue;

			switch(true) {
				case disconnectedDevices[macAddress].value >= low_disconnect_range_min && disconnectedDevices[macAddress].value < low_disconnect_range_max:
					maintenanceData.low_disconnected_devices.value++;
					break;
				case disconnectedDevices[macAddress].value >= mid_disconnect_range_min && disconnectedDevices[macAddress].value < mid_disconnect_range_max:
					maintenanceData.mid_disconnected_devices.value++;
					break;
				case disconnectedDevices[macAddress].value >= high_disconnect_limit:
					maintenanceData.high_disconnected_devices.value++;
					break;
				default:
					break;
			}
		}

		const maintenanceMetricsData = [];
		for (const key in maintenanceData) {
			const value =totalDevicesCount ?  Math.round(((maintenanceData[key].value / totalDevicesCount) * 100)) : 0;

			maintenanceMetricsData.push({
				label: maintenanceData[key].label,
				value: value,
				percentage: value,
				showPercentage: true,
				name: 'disconnected_chargers',
			})
		}

		this.maintenance = maintenanceMetricsData;
	}

	calculateScore(chartType: string): number {
		const widgetDataKey = this.widgetsDataMapper[chartType];
		let selectedData = (this as any)[widgetDataKey]?.[this.filters.selectedPeriod];
		const siteDevices = keyBy(this.devices, 'mac_address');

		// ST-2689: only chargers with a usable location (GPS or estimated) are judged against the geofence
		if (chartType == 'chargers_outside_geofence')
			selectedData = keyBy(this.devices.filter(device => device.has_location), 'mac_address');
		
		const totalDevicesCount = this.devices.filter(device => device.mac_address in (selectedData || {}) && this.isIncludedDevice(device.mac_address)).length;

		let totalReportDevices = 0;
		for (const macAddress in selectedData) {
			if (chartType == 'chargers_outside_geofence' && this.isIncludedDevice(macAddress) && siteDevices[macAddress]?.is_far_device)
				totalReportDevices++;
			else if (this.isIncludedDevice(macAddress) && selectedData[macAddress]?.showDevice)
				totalReportDevices++;
		}

		return totalDevicesCount ? Math.round((totalReportDevices / totalDevicesCount) * 100) : 0;
	}

	isIncludedDevice(macAddress: string) {
		const siteDevices = keyBy(this.devices, 'mac_address');

		if (this.filters.selectedVoltage !== 'all' && siteDevices?.[macAddress]?.charger_voltage !== this.filters.selectedVoltage)
			return false;

		if (!this.siteDashboardService.isVintageWithinFilter(siteDevices?.[macAddress], this.filters.selectedVintage))
			return false;

		if (this.filters.selectedManufacturer !== 'all' && siteDevices?.[macAddress]?.truck_manufacturer !== this.filters.selectedManufacturer)
			return false;

		return true;
	}

	vintageSelectionChanged() {
		if (!this.filters.selectedVintage.length)
			this.filters.selectedVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (this.filters.selectedVintage.length > 1 && this.filters.selectedVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.filters.selectedVintage[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.filters.selectedVintage = this.filters.selectedVintage.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.filters.selectedVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}

		this.formatUtilizationData();
	}
	
	exportReports() {
		const data = this.prepareDataForExcelExport();
		this.excelService.exportExcel(data, `Exception Report.xlsx`);
	}

	prepareDataForExcelExport(): ExcelSheet[] {
		const devices = keyBy(this.devices, 'mac_address');

		const csvHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo?.customer_name,
			siteName: this.siteInfo?.name,
			includeSpacing: false
		}) || [];

		const headerInfoBlock: any[][] = [
			...csvHeaderInfo,
			[this.translateService.instant('exception_reports.time_period'), this.getSelectedPeriodLabel()],
			[this.translateService.instant('exception_reports.selected_filter_values')],
			...this.getSelectedFilterValuesRows(),
			[],
		];

		const sheetData: any[][] = [...headerInfoBlock];

		const headerInfoRows: number[] = [];
		headerInfoBlock.forEach((row, idx) => {
			if (row && row.length) headerInfoRows.push(idx);
		});

		const titleRows: number[] = [];
		const tables: any[] = [];

		let counter = 0
		for (const widgetKey in this.widgetsDataMapper) {
			const widgetDataKey = this.widgetsDataMapper[widgetKey];
			const widgetConfig = this.widgetsConfig[widgetKey];
			const widgetData = (this as any)[widgetDataKey]?.[this.filters.selectedPeriod];

			const staticFields = this.staticColumnConfig.map((field: any) => field.name);

			const header = [
				...staticFields,
				...widgetConfig.extra_fields.map((field: string) => this.translateService.instant(`exception_reports.${field}`))
			];

			if(counter) //ignore extra empty row in the first
				sheetData.push([]);

			const titleRowIndex = sheetData.length;
			sheetData.push([widgetConfig.title]);
			titleRows.push(titleRowIndex);

			const headerRowIndex = sheetData.length;
			sheetData.push(header);

			const dataStartRow = sheetData.length;

			const percentColumns = this.getPercentColumnIndexes(widgetKey, staticFields.length);

			const widgetDevicesData = widgetData || devices;

			for (const macAddress in widgetDevicesData) {
				const device = devices[macAddress] || {};

				if (!this.isIncludedDevice(macAddress) || (!widgetData?.[macAddress]?.showDevice && widgetKey != 'chargers_outside_geofence') || (widgetKey == 'chargers_outside_geofence' && !device.is_far_device))
					continue;

				const recordRow: any[] = this.staticColumnConfig.map((field: any) => device?.[field.key] || '');

				if (widgetData && (widgetKey != 'chargers_outside_geofence' && widgetKey != 'days_charger_disconnected')) {
					recordRow.push(this.normalizePercentValue(widgetKey, widgetData[macAddress].value));
				} else {
					for (const field of widgetConfig.extra_fields) {
						const deviceField = field == 'last_charger_location_long' ? 'last_charger_location_long' : field == 'last_charger_location_lat' ? 'last_charger_location_lat' : field;
						if (deviceField == 'days_outside_geofence' && device.is_estimated_location)
							recordRow.push(this.translateService.instant('g.n/a'));
						else
							recordRow.push(device[deviceField]);
					}
				}

				sheetData.push(recordRow);
			}

			const dataEndRow = sheetData.length - 1;

			if (dataEndRow >= dataStartRow) {
				tables.push({
					headerRow: headerRowIndex,
					dataStartRow,
					dataEndRow,
					startCol: 0,
					endCol: header.length - 1,
					percentColumns,
				});
			} else {
				const noDataRowIndex = sheetData.length;
				const noDataRow = new Array(header.length).fill('');
				noDataRow[0] = this.translateService.instant('g.no_data_to_show');
				sheetData.push(noDataRow);

				tables.push({
					headerRow: headerRowIndex,
					dataStartRow: noDataRowIndex,
					dataEndRow: noDataRowIndex,
					startCol: 0,
					endCol: header.length - 1,
					percentColumns: [],
					noData: true,
				});
			}

			counter++;
		}

		return [{
			data: sheetData,
			sheetName: this.translateService.instant('exception_reports.charger_exception_reports'),
			style: {
				headerInfoRows,
				titleRows,
				tables,
			},
		}];
	}

	normalizePercentValue(widgetKey: string, value: any): any {
		if (!this.PERCENT_WIDGETS.has(widgetKey) || value == null || isNaN(value))
			return value;

		const num = Number(value);
		return num / 100;
	}

	getSelectedPeriodLabel(): string {
		return this.periods.find(p => p.value === this.filters.selectedPeriod)?.label || this.filters.selectedPeriod;
	}

	getSelectedFilterValuesRows(): any[][] {
		const allLabel = this.translateService.instant('g.all');

		const voltage = this.filters.selectedVoltage === 'all'
			? allLabel : this.deviceCommonService.getChargerVoltageLabel(this.filters.selectedVoltage);
		const manufacturer = this.filters.selectedManufacturer === 'all'
			? allLabel : this.filters.selectedManufacturer;

		return [
			[this.translateService.instant('devices.charger_voltage'), voltage],
			[this.translateService.instant('devices.charger_manufacturer'), manufacturer],
		];
	}

	getPercentColumnIndexes(widgetKey: string, staticFieldCount: number): number[] {
		if (!this.PERCENT_WIDGETS.has(widgetKey))
			return [];

		return [staticFieldCount];
	}

	get isNoData() {
		return !this.assetUtilization.some(metric => metric.value) && !this.maintenance.some(metric => metric.value);
	}

	ngOnDestroy() {
		if (this.siteSub)
			this.siteSub.unsubscribe();
	}
}
